import React, { useEffect, useState } from "react"
import {
  Dimensions,
  Image,
  Modal,
  ScrollView,
  TouchableOpacity,
  View,
} from "react-native"
import { Body, Small } from "@/components/standard/BFText"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { useQuery } from "@tanstack/react-query"
import { sdk } from "@/api/sdk"
import XIcon from "phosphor-react-native/src/icons/X"
import ChevronLeftIcon from "phosphor-react-native/src/icons/CaretLeft"
import ChevronRightIcon from "phosphor-react-native/src/icons/CaretRight"

interface PropertyImageLightboxProps {
  visible: boolean
  onClose: () => void
  analysisId?: string
  compId?: string
  fallbackProperty?: {
    fullAddress?: string
    heroImage?: string
  }
}

export function PropertyImageLightbox({
  visible,
  onClose,
  analysisId,
  compId,
  fallbackProperty,
}: PropertyImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const { width: screenWidth, height: screenHeight } = Dimensions.get("window")

  // Only fetch comp data if we have valid IDs
  const hasValidIds = !!(analysisId && compId && compId.length > 10 && visible)

  const { data: compData, error } = useQuery({
    queryKey: ["comp", analysisId, compId],
    queryFn: async () => {
      try {
        return await sdk.GetCompDetail({
          analysisId: analysisId!,
          arvCompId: compId!,
        })
      } catch (err) {
        console.error("Error fetching comp:", err)
        throw err
      }
    },
    enabled: hasValidIds,
    retry: false, // Don't retry on UUID validation errors
  })

  // Debug logging for errors only
  useEffect(() => {
    if (visible && error) {
      console.error("PropertyImageLightbox error:", error)
    }
  }, [visible, error])

  // Reset current index when modal opens
  useEffect(() => {
    if (visible) {
      setCurrentIndex(0)
    }
  }, [visible])

  // Collect all available images following CompDetailImages pattern
  const images: string[] = []
  const arvcomp = compData?.analysisComp?.arvcomp

  if (
    arvcomp?.media &&
    Array.isArray(arvcomp.media) &&
    arvcomp.media.length > 0
  ) {
    // Use media array if available (from comp detail)
    images.push(...arvcomp.media)
  } else if (arvcomp?.heroImage || fallbackProperty?.heroImage) {
    // Fallback to hero image from either source
    const heroImage = arvcomp?.heroImage || fallbackProperty?.heroImage
    if (heroImage) {
      images.push(heroImage)
    }
  }

  const property = arvcomp || fallbackProperty

  // If no images, don't show the modal
  if (images.length === 0) {
    return null
  }

  const modalWidth = screenWidth * 0.8
  const modalHeight = screenHeight * 0.8
  const imageHeight = modalHeight * 0.7
  const thumbnailHeight = 60

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  const handleBackdropPress = () => {
    onClose()
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      {/* Backdrop */}
      <TouchableOpacity
        testID="lightbox-backdrop"
        activeOpacity={1}
        onPress={handleBackdropPress}
        className="flex-1 bg-black/70 justify-center items-center"
      >
        {/* Modal Content */}
        <TouchableOpacity
          testID="lightbox-modal-content"
          activeOpacity={1}
          onPress={(e) => {
            e.stopPropagation()
          }}
          style={{
            width: modalWidth,
            height: modalHeight,
          }}
          className="bg-card rounded-lg overflow-hidden"
        >
          <Vertical className="flex-1">
            {/* Header */}
            <Horizontal className="items-center justify-between p-4 border-b border-border">
              <Body className="flex-1 font-medium" numberOfLines={1}>
                {property?.fullAddress || "Property Images"}
              </Body>

              <TouchableOpacity
                testID="lightbox-close"
                onPress={onClose}
                className="ml-2 p-1"
              >
                <XIcon size={20} color="#676080" />
              </TouchableOpacity>
            </Horizontal>

            {/* Main Image Area */}
            <View className="flex-1 relative">
              <Image
                source={{ uri: images[currentIndex] }}
                style={{
                  width: "100%",
                  height: imageHeight,
                }}
                resizeMode="contain"
                className="bg-secondary"
              />

              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <TouchableOpacity
                    testID="lightbox-prev"
                    onPress={goToPrevious}
                    className="absolute left-2 top-1/2 -mt-4 bg-black/50 p-2 rounded-full"
                  >
                    <ChevronLeftIcon size={16} color="white" />
                  </TouchableOpacity>

                  <TouchableOpacity
                    testID="lightbox-next"
                    onPress={goToNext}
                    className="absolute right-2 top-1/2 -mt-4 bg-black/50 p-2 rounded-full"
                  >
                    <ChevronRightIcon size={16} color="white" />
                  </TouchableOpacity>
                </>
              )}

              {/* Image Counter */}
              {images.length > 1 && (
                <View className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded">
                  <Small className="text-white text-xs">
                    {currentIndex + 1} / {images.length}
                  </Small>
                </View>
              )}
            </View>

            {/* Thumbnails */}
            {images.length > 1 && (
              <View className="border-t border-border p-3">
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={{ gap: 8 }}
                >
                  {images.map((image, index) => (
                    <TouchableOpacity
                      key={index}
                      testID={`lightbox-thumbnail-${index}`}
                      onPress={() => {
                        setCurrentIndex(index)
                      }}
                      className={`rounded overflow-hidden ${
                        index === currentIndex
                          ? "border-2 border-primary"
                          : "border border-border"
                      }`}
                    >
                      <Image
                        source={{ uri: image }}
                        style={{
                          width: thumbnailHeight,
                          height: thumbnailHeight,
                        }}
                        resizeMode="cover"
                      />
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
          </Vertical>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  )
}
